import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeModeProvider } from './ThemeContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { PermissionProvider } from './contexts/PermissionContext';
import AppRoutes from './routes/routes';
import GoogleAnalytics from './components/GoogleAnalytics';
import { useThemeMode } from './ThemeContext';
import { useForegroundMessages } from './firebase';
import { formatNotificationPayload } from './firebase';
import appIcon from './assets/appicon.png';
import sessionManager from './helpers/session_manager';
import { OAUTH_CLIENTS } from './helpers/url_helper';

// Inner App component that has access to loading context
const AppContent = () => {
  const { mode } = useThemeMode();
  const [inAppNotification, setInAppNotification] = useState(null);
  
  // Handle foreground messages
  useForegroundMessages((payload) => {
    const data = formatNotificationPayload(payload);
    setInAppNotification({
      title: data.title,
      body: data.body,
    });
    try {
      window.dispatchEvent(new CustomEvent('complimate:notification', { detail: data }));
    } catch (e) {
      // noop
    }
  });

  // Listen for messages from service worker (for background notifications)
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === 'FIREBASE_NOTIFICATION') {
        console.log('Received notification from service worker:', event.data.payload);
        const data = formatNotificationPayload(event.data.payload);
        setInAppNotification({
          title: data.title,
          body: data.body,
        });
        try {
          window.dispatchEvent(new CustomEvent('complimate:notification', { detail: data }));
        } catch (e) {
          // noop
        }
      }
    };

    // Listen for messages from service worker
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  // Auto-dismiss notification after 10 seconds
  useEffect(() => {
    let timeoutId;
    if (inAppNotification) {
      timeoutId = setTimeout(() => {
        setInAppNotification(null);
      }, 10000); // 10 seconds
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [inAppNotification]);

  const bannerStyles = useMemo(() => {
    const isDark = mode === 'dark';
    return {
      container: {
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 4000,
        maxWidth: 360,
        backgroundColor: isDark ? '#0b0f12' : '#ffffff',
        border: `1px solid ${isDark ? '#1f2a32' : '#e6f2f1'}`,
        borderLeft: '4px solid #26C3C0',
        boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(38,195,192,0.25)',
        borderRadius: 12,
        padding: 14,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      },
      title: {
        margin: 0,
        color: isDark ? '#e9f6f6' : '#0a2625',
        fontSize: 14,
        fontWeight: 700,
      },
      body: {
        margin: '4px 0 0 0',
        color: isDark ? '#cde7e6' : '#214b49',
        fontSize: 13,
        lineHeight: 1.4,
      },
      close: {
        background: 'transparent',
        border: 'none',
        color: isDark ? '#9acdcc' : '#1b6f6c',
        cursor: 'pointer',
        padding: 4,
        marginLeft: 8,
        fontSize: 14,
      }
    };
  }, [mode]);

  return (
    <>
      <GoogleAnalytics />
      <AppRoutes />
      {inAppNotification && (
        <div style={bannerStyles.container} role="status" aria-live="polite">
          <img src={appIcon} alt="App" width={28} height={28} style={{ marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <p style={bannerStyles.title}>{inAppNotification.title}</p>
            <p style={bannerStyles.body}>{inAppNotification.body}</p>
          </div>
          <button
            aria-label="Dismiss notification"
            style={bannerStyles.close}
            onClick={() => setInAppNotification(null)}
          >
            Dismiss
          </button>
        </div>
      )}
    </>
  );
};

function App() {
  // Initialize session manager when app starts
  useEffect(() => {
    // Session manager is automatically initialized when imported
    // This ensures it's ready to handle authentication checks
  }, []);

  return (
    <GoogleOAuthProvider clientId={OAUTH_CLIENTS.GOOGLE}>
      <ThemeModeProvider>
        <LoadingProvider>
          <PermissionProvider>
            <Router>
              <AppContent />
            </Router>
          </PermissionProvider>
        </LoadingProvider>
      </ThemeModeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
